import { useEffect, useState } from "react";
import { Modal } from "@mantine/core";
import { ViewFileView } from "shared/src/components/document-upload-modal";
import { documentTypeDisplayValueMap } from "../../types/document";
import {
  AppealSupportingDocumentFile,
  AppealSupportingDocumentType,
} from "@/__generated__/graphql.ts";
import { downloadFile } from "@/utils/file-requests.ts";
import { getFileUrlsForFile } from "@/utils/file-conversion.ts";

type ViewDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: AppealSupportingDocumentFile[];
  documentType: AppealSupportingDocumentType;
  otherDocumentTypeName?: string;
};

export const ViewDocumentModal = ({
  isOpen,
  onClose,
  files,
  documentType,
  otherDocumentTypeName,
}: ViewDocumentModalProps) => {
  const [fileUrls, setFileUrls] = useState<string[] | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [sizeOfFiles, setSizeOfFiles] = useState<number | null>(null);
  const [hasErrorLoadingFiles, setHasErrorLoadingFiles] = useState(false);

  useEffect(() => {
    const loadfiles = async () => {
      try {
        const downloadedFiles = await Promise.all(
          files.map((file) => downloadFile(file.url)),
        );
        const blobFileUrls = (
          await Promise.all(downloadedFiles.map(getFileUrlsForFile))
        ).flat();
        const sizeOfDownloadedFiles = downloadedFiles.reduce(
          (totalSize, file) => totalSize + file.size,
          0,
        );

        setFileUrls(blobFileUrls);
        setSizeOfFiles(sizeOfDownloadedFiles);
      } catch (error) {
        /* oxlint-disable-next-line no-console */
        console.error("Error loading files", error);
        setHasErrorLoadingFiles(true);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    if (isOpen) {
      setHasErrorLoadingFiles(false);
      setFileUrls(null);
      setIsLoadingFiles(true);
      loadfiles();
    }
  }, [isOpen, files]);

  return (
    <Modal opened={isOpen} onClose={onClose} size="xl">
      <ViewFileView
        title={
          documentType === AppealSupportingDocumentType.Other &&
          otherDocumentTypeName
            ? otherDocumentTypeName
            : documentTypeDisplayValueMap[documentType]
        }
        fileUrls={fileUrls ?? []}
        isLoading={isLoadingFiles}
        hasError={hasErrorLoadingFiles}
        size={sizeOfFiles ?? 0}
      />
    </Modal>
  );
};
